import type { Component } from "solid-js";
import { createSignal, Show, untrack } from "solid-js";
import posthog from "posthog-js";
import { TextAndIconButton } from "../../../components/button/Button";

import TasksTableView from "./TasksTableView";
import { useTranslation } from "../../../state/i18n";
import type CustomerId from "../../../state/CustomerId";
import TableFilterButton from "./TableFilterButton";
import CreateTaskWizard from "../CreateTaskWizard";
import { useModalManager } from "../../../components/modal/ModalManager";
import ApiService from "../../../services/ApiService";
import type { Form } from "../../../state/form";
import { toVariableConfig } from "../../../utils/mappers/formVersion";
import UserService from "../../../services/UserService";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import type FormVersionId from "../../../state/FormVersionId";
import { isNil } from "lodash-es";
import { TableProvider, useTable } from "./TableProvider";
import PermissionService from "../../../services/PermissionService.ts";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";
import { getGroupsWithFormAccess, getUsersWithFormAccess } from "../../../utils/permissionUtil";

type TasksContentProps = {
  customerId: CustomerId;
  form: Form;
};

const TasksContent: Component<TasksContentProps> = (props) => {
  const { t } = useTranslation();
  const [isCreateTaskLoading, setIsCreateTaskLoading] = createSignal<boolean>(false);

  const { open } = useModalManager();

  const customerId = untrack(() => props.customerId);
  const usersQuery = UserService.getUsers(customerId);
  const groupsQuery = UserService.getGroups(customerId);

  const openWizard = async (publishedFormVersionId: FormVersionId) => {
    setIsCreateTaskLoading(true);

    const formVariables = await ApiService.getFormVersion(props.form.id, publishedFormVersionId)
      .then((formVersion) =>
        (formVersion.variables ?? []).map((variableConfigDto) =>
          toVariableConfig(
            variableConfigDto,
            untrack(() => props.form),
          ),
        ),
      )
      .finally(() => setIsCreateTaskLoading(false));

    const users = usersQuery.data ?? [];
    const groups = groupsQuery.data ?? [];

    const groupsWithAccess = getGroupsWithFormAccess(groups, props.form.id);
    const usersWithAccess = getUsersWithFormAccess(users, groups, props.form.id);

    return open({
      content: () => (
        <CreateTaskWizard
          users={usersWithAccess}
          groups={groupsWithAccess}
          formVariables={formVariables}
          formId={props.form.id}
          formVersionId={publishedFormVersionId}
        />
      ),
      onClose: () => posthog.capture(POSTHOG_EVENTS.TASK_CREATION_CANCELED),
    });
  };

  return (
    <Show
      when={props.form.publishedVersion?.formVersionId}
      fallback={
        <Show
          when={isNil(props.form.latestVersion)}
          fallback={<InlineMessage variant="warning" label={t(($) => $.CREATE_TASK_NO_USER_GROUPS_WARNING)} />}
        >
          <InlineMessage variant="warning" label={t(($) => $.CREATE_TASK_FORM_NOT_PUBLISHED_WARNING)} />
        </Show>
      }
    >
      {(publishedFormVersionId) => (
        <TableProvider>
          <TasksContentBody
            publishedFormVersionId={publishedFormVersionId()}
            form={props.form}
            customerId={props.customerId}
            isCreateTaskLoading={isCreateTaskLoading()}
            openWizard={openWizard}
          />
        </TableProvider>
      )}
    </Show>
  );
};

type TasksContentBodyProps = {
  publishedFormVersionId: FormVersionId;
  form: Form;
  customerId: CustomerId;
  isCreateTaskLoading: boolean;
  openWizard: (publishedFormVersionId: FormVersionId) => Promise<void>;
};

const TasksContentBody: Component<TasksContentBodyProps> = (props) => {
  const { t } = useTranslation();
  const { isEmpty } = useTable();

  return (
    <>
      <div class="flex justify-between">
        <Show when={!isEmpty()} fallback={<div />}>
          <TableFilterButton />
        </Show>
        <Show when={PermissionService.canCreateFormTasks(props.customerId, props.form.id)}>
          <div class="flex gap-x-2">
            <TextAndIconButton
              text={t(($) => $.TASKS_CREATE_TASK_BUTTON_LABEL)}
              icon="plus"
              size="large"
              variant="light"
              onClick={() => props.openWizard(props.publishedFormVersionId)}
              loading={props.isCreateTaskLoading}
            />
          </div>
        </Show>
      </div>
      <TasksTableView customerId={props.customerId} formId={props.form.id} formName={props.form.meta.name} />
    </>
  );
};

export default TasksContent;
